import React, { useState } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import axios from "axios";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import {
  FaUserTie,
  FaBullhorn,
  FaMicrophone,
  FaStore,
  FaLaptop,
  FaShareAlt,
  FaChartBar,
  FaDollarSign,
} from "react-icons/fa";
import Loader from "../Components/Loader";

const API_BASE = process.env.REACT_APP_API_BASE;

const SponsorForm = () => {
  const [country] = useState("us");
  const [loading, setLoading] = useState(false);

  // ✅ ONLY validation updated (NO UI impact)
  const validationSchema = Yup.object({
    firstName: Yup.string().required("**First Name is required"),
    lastName: Yup.string().required("**Last Name is required"),
    company: Yup.string().required("**Company is required"),
    email: Yup.string().email("**Invalid email").required("**Email is required"),
    phone: Yup.string().required("**Phone is required"),
  });

  // ✅ ONLY endpoint updated
  const handleSubmit = async (values, { resetForm }) => {
    try {
      setLoading(true);
      const res = await axios.post(`${API_BASE}/fsponsors`, values);

      if (res.data.success) {
        alert(res.data.message || "Form submitted successfully!");
        resetForm();
      } else {
        alert(res.data.error || "Submission failed. Please try again.");
      }
    } catch (err) {
      console.error(err);
      alert("Something went wrong! Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {loading && <Loader />}
      <section
        className="pb-0 overflow-visible d-block position-relative z-1"
        style={{ marginBottom: "100px", padding: "80px 0", fontFamily: "'Inter', sans-serif" }}
      >
        <div className="container">
          <div className="row">
            {/* LEFT SIDE */}
            <div className="col-lg-8 px-4 mb-2">
              <h2
                style={{
                  fontWeight: "800",
                  marginBottom: "20px",
                  fontSize: "2.2rem",
                  letterSpacing: "-0.5px",
                }}
              >
                Benefits of Sponsors
              </h2>

              <div className="row g-4">
                {[
                  { icon: <FaUserTie size={22} />, title: "Direct Access to Decision-Makers", desc: "Showcase your solutions to senior leaders from operators, service firms, regulators, and technology providers." },
                  { icon: <FaBullhorn size={22} />, title: "Brand Amplification", desc: "Prominent logo placement on the event site, agenda, email blasts, banners, and during live sessions." },
                  { icon: <FaMicrophone size={22} />, title: "Sponsored Speaking Opportunity", desc: "Deliver a keynote, join a panel, or host a fireside chat as part of your sponsorship tier." },
                  { icon: <FaStore size={22} />, title: "Virtual Showcase & Booth", desc: "Display brochures, case studies, videos, and contact links in your sponsor profile." },
                  { icon: <FaLaptop size={22} />, title: "Live Product Demos", desc: "Schedule live demo sessions and host 1-on-1 breakout chats with qualified leads." },
                  { icon: <FaShareAlt size={22} />, title: "Social Media Recognition", desc: "Get featured in pre- and post-event posts across LinkedIn, X (Twitter), and newsletters." },
                  { icon: <FaChartBar size={22} />, title: "Post-Event Reporting", desc: "Receive a customized report with attendee demographics, session engagement, and click-through data." },
                  { icon: <FaDollarSign size={22} />, title: "Cost-Effective ROI", desc: "Eliminate booth logistics and travel expenses while achieving high visibility and measurable impact." },
                ].map((item, index) => (
                  <div key={index} className="col-md-6 d-flex">
                    <div
                      style={{
                        width: "54px",
                        height: "54px",
                        background: "linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)",
                        color: "#fff",
                        borderRadius: "12px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        marginRight: "14px",
                        boxShadow: "0 4px 10px rgba(37,117,252,0.35)",
                      }}
                    >
                      {item.icon}
                    </div>
                    <div>
                      <h5 style={{ fontWeight: "700", fontSize: "1.05rem", marginBottom: "6px" }}>
                        {item.title}
                      </h5>
                      <p style={{ fontSize: "0.95rem", lineHeight: "1.7", color: "#555", margin: 0 }}>
                        {item.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* RIGHT SIDE FORM */}
            <div className="col-lg-4 px-4">
              <div
                style={{
                  position: "sticky",
                  top: "100px",
                  background: "#fff",
                  zIndex: 100,
                  boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
                  borderRadius: "16px",
                  padding: "30px",
                }}
              >
                <h4 style={{ fontWeight: "700", marginBottom: "20px", fontSize: "1.3rem", color: "#111" }}>
                  Have a topic you want to share?
                </h4>

                <Formik
                  initialValues={{
                    firstName: "",
                    lastName: "",
                    company: "",
                    email: "",
                    phone: "",
                  }}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                >
                  {({ isSubmitting, setFieldValue, values }) => (
                    <Form>
                      <Field
                        name="firstName"
                        placeholder="First Name *"
                        className="form-control mb-3"
                        style={{ borderRadius: "10px", padding: "12px", fontSize: "0.95rem" }}
                      />
                      <ErrorMessage name="firstName" component="div" style={{ color: "red", fontSize: "0.8rem" }} />

                      <Field
                        name="lastName"
                        placeholder="Last Name *"
                        className="form-control mb-3"
                        style={{ borderRadius: "10px", padding: "12px", fontSize: "0.95rem" }}
                      />
                      <ErrorMessage name="lastName" component="div" style={{ color: "red", fontSize: "0.8rem" }} />

                      {/* ✅ ONLY NEW FIELD — SAME STYLE */}
                      <Field
                        name="company"
                        placeholder="Company *"
                        className="form-control mb-3"
                        style={{ borderRadius: "10px", padding: "12px", fontSize: "0.95rem" }}
                      />
                      <ErrorMessage name="company" component="div" style={{ color: "red", fontSize: "0.8rem" }} />

                      <Field
                        name="email"
                        type="email"
                        placeholder="Email address *"
                        className="form-control mb-3"
                        style={{ borderRadius: "10px", padding: "12px", fontSize: "0.95rem" }}
                      />
                      <ErrorMessage name="email" component="div" style={{ color: "red", fontSize: "0.8rem" }} />

                      <div className="mb-3">
                        <PhoneInput
                          country={country}
                          value={values.phone}
                          onChange={(val) => setFieldValue("phone", val)}
                          enableSearch
                          placeholder="Phone *"
                          inputStyle={{
                            width: "100%",
                            height: "50px",
                            borderRadius: "10px",
                            paddingLeft: "60px",
                            fontSize: "1rem",
                            border: "1px solid #ccc",
                          }}
                          buttonStyle={{ borderRadius: "10px 0 0 10px", border: "none", background: "transparent" }}
                          containerStyle={{ width: "100%" }}
                        />
                        <ErrorMessage name="phone" component="div" style={{ color: "red", fontSize: "0.8rem" }} />
                      </div>

                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="btn w-100"
                        style={{
                          borderRadius: "10px",
                          padding: "12px",
                          fontWeight: "600",
                          fontSize: "1rem",
                          background: "linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)",
                          color: "#fff",
                          border: "none",
                          boxShadow: "0 4px 10px rgba(106,17,203,0.3)",
                        }}
                      >
                        {isSubmitting ? "Submitting..." : "Sign Up"}
                      </button>

                      <p style={{ marginTop: "15px", fontSize: "0.8rem", color: "#6c757d", lineHeight: "1.5" }}>
                        By proceeding, you're agreeing to receive communications from Futureoilconference and/or its
                        affiliates, in accordance with our Terms of Use and Privacy Policy.
                      </p>
                    </Form>
                  )}
                </Formik>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SponsorForm;
